import { useFetcher, useLocation, useNavigate } from '@remix-run/react'
import type { ReactElement } from 'react'
import { useEffect, useState } from 'react'
import BreadCrumbsList from './BreadCrumbList'
import {
  FormLegalPersonalData,
  FormNaturalPersonalData,
  LegalPersonalData,
  NaturalPersonalData,
} from '~/design-system/components'
import { Button, Card, Loading } from '~/design-system/primitives'
import { GlobalToastProvider } from '~/hooks/useGlobalToast'
import { useScrollToTop } from '~/hooks/useScrollToTop'
import { ActionEdit, ClinicalNotes, PersonPin } from '~/icons'
import { cn } from '~/lib/cn'
import type { Profile } from '~/type/User'


interface ResponseDataPersonalData {
  response: Profile
  success: boolean
}

export default function MyPersonalData() {
  const [componentRendered, setComponentRendered] = useState<ReactElement>()
  const [editComponentRendered, setEditComponentRendered] = useState<ReactElement>()
  const [isLoading, setIsLoading] = useState(false)
  const fetcherGet = useFetcher()
  const fetcherSend = useFetcher()
  const navigate = useNavigate()
  const location = useLocation()
  const editPersonalData = location.pathname.includes('/editar')

  useScrollToTop({ isLoading, componentRendered })

  const fetcherGetPersonalData = () => {
    setIsLoading(true)
    fetcherGet.submit({ actionType: 'getPersonalData' }, { method: 'post', action: '/myaccount' })
  }

  useEffect(() => {
    fetcherGetPersonalData()
  }, [])

  useEffect(() => {
    const data = fetcherGet.data as ResponseDataPersonalData
    if (data && data.response) {
      setIsLoading(false)
      setComponentRendered(
        data.response.isCorporate === true ? (
          <LegalPersonalData profile={data.response} />
        ) : (
          <NaturalPersonalData profile={data.response} />
        ),
      )

      setEditComponentRendered(
        data.response.isCorporate === true ? (
          <FormLegalPersonalData
            fetcherGetPersonalData={fetcherGetPersonalData}
            profile={data.response}
            setEditPersonalData={() => navigate('/myaccount/dados-cadastrais')}
          />
        ) : (
          <FormNaturalPersonalData
            fetcherGetPersonalData={fetcherGetPersonalData}
            profile={data.response}
            setEditPersonalData={() => navigate('/myaccount/dados-cadastrais')}
          />
        ),
      )
    }
  }, [fetcherGet.data])

  useEffect(() => {
    const data = fetcherSend.data as ResponseDataPersonalData
    if (data && data.response) {
      setIsLoading(false)
    }
  }, [fetcherGet.data])

  return (
    <>
      <div className="flex w-full flex-col gap-4 sm:mainContent">
        {editPersonalData && <BreadCrumbsList />}
        <Card className={cn('lg:w-6/6 self-stretch ')} color="primary" border>
          {editPersonalData ? (
            <Card className={`parentMenu flex-row justify-between p-2`}>
              <Card className={`items-center flex flex-row p-0`}>
                <h2 className={`font-medium flex gap-3 w-full`}>
                  <ClinicalNotes />
                  <span className="text-primary-black text-base ml-2">Editar Dados</span>
                </h2>
              </Card>
            </Card>
          ) : (
            <Card className={`parentMenu flex-row justify-between p-2`}>
              <Card className={`items-center flex flex-row p-0`}>
                <h2 className={`font-medium flex items-center gap-3 w-full`}>
                  <PersonPin />
                  <span className="text-primary-black text-base ml-2">Meus Dados</span>
                </h2>
              </Card>
              <Button
                className="flex order-3 items-center p-0 m-1"
                onPress={() => {
                  navigate('/myaccount/dados-cadastrais/editar')
                }}
              >
                <ActionEdit className="cursor-pointer" />
              </Button>
            </Card>
          )}
          <hr className="flex items-center self-stretch divide-y divide-solid border-gray-20 mt-1" />
          {editPersonalData ? (
            <GlobalToastProvider>
              <>{editComponentRendered}</>
            </GlobalToastProvider>
          ) : (
            <>{componentRendered}</>
          )}
        </Card>
      </div>
      {isLoading && <Loading />}
    </>
  )
}
